import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useNavigate } from 'react-router-dom';

const AccountSettings = () => {
  const { user, logout } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [businessInfo, setBusinessInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [competitors, setCompetitors] = useState([]);
  const [newCompetitor, setNewCompetitor] = useState({ name: '', domain: '' });
  const [loadingCompetitors, setLoadingCompetitors] = useState(false);
  const [addingCompetitor, setAddingCompetitor] = useState(false);

  useEffect(() => {
    fetchUserInfo();
    fetchCompetitors();
  }, []);

  const fetchUserInfo = async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      const response = await fetch(`${apiUrl}/users/profile`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setBusinessInfo({
          name: data.business_name || '',
          website: data.business_website || ''
        });
      } else {
        throw new Error('Failed to fetch user profile');
      }
    } catch (error) {
      console.error('Failed to fetch user info:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompetitors = async () => {
    try {
      setLoadingCompetitors(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      const response = await fetch(`${apiUrl}/users/competitors`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCompetitors(data.data.competitors || []);
      }
    } catch (error) {
      console.error('Failed to fetch competitors:', error);
    } finally {
      setLoadingCompetitors(false);
    }
  };

  const handleAddCompetitor = async () => {
    if (!newCompetitor.name.trim()) {
      setError('Please enter a competitor name');
      return;
    }

    try {
      setAddingCompetitor(true);
      setError(null);
      setSuccess(null);
      
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      const response = await fetch(`${apiUrl}/users/competitors`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          competitor_name: newCompetitor.name,
          competitor_domain: newCompetitor.domain || null
        })
      });
      
      const data = await response.json();
      
      if (response.ok) {
        setSuccess('Competitor added successfully');
        setCompetitors([...competitors, data.data.competitor]);
        setNewCompetitor({ name: '', domain: '' });
      } else {
        setError(data.error || 'Failed to add competitor');
      }
    } catch (error) {
      console.error('Failed to add competitor:', error);
      setError('Failed to add competitor');
    } finally {
      setAddingCompetitor(false);
    }
  };

  const handleRemoveCompetitor = async (competitorId) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';
      const response = await fetch(`${apiUrl}/users/competitors/${competitorId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        }
      });
      
      if (response.ok) {
        setSuccess('Competitor removed successfully');
        setCompetitors(competitors.filter(c => c.id !== competitorId));
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to remove competitor');
      }
    } catch (error) {
      console.error('Failed to remove competitor:', error);
      setError('Failed to remove competitor');
    }
  };


  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] dashboard-optimized">
        <div className="glass-spinner w-12 h-12 mb-4"></div>
        <p className={`text-mono text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fade-in-scale">
      {/* Page Header */}
      <div>
        <h1 className={`heading-mono text-2xl font-bold text-glow ${isDark ? 'text-white' : 'text-gray-800'}`}>
          Account Settings
        </h1>
        <p className={`text-mono text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          Manage your account preferences and configuration
        </p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="glass-alert-success rounded-lg p-4 animate-slide-in-up">
          <div className="flex items-center gap-3">
            <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <p className={`text-mono text-sm ${isDark ? 'text-green-300' : 'text-green-800'}`}>{success}</p>
          </div>
        </div>
      )}

      {error && (
        <div className="glass-alert-error rounded-lg p-4 animate-slide-in-up">
          <div className="flex items-center gap-3">
            <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <p className={`text-mono text-sm ${isDark ? 'text-red-300' : 'text-red-800'}`}>{error}</p>
          </div>
        </div>
      )}

      {/* Business Information */}
      <div className="glass-card rounded-2xl p-8 dashboard-optimized">
        <h3 className={`heading-mono text-lg text-glow mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
          Business Information
        </h3>
        <p className={`text-mono text-sm leading-relaxed mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          Your business information used for competitor analysis. This information cannot be changed after initial setup.
        </p>
        
        <div className="space-y-4">
          <div>
            <label className={`block text-mono text-xs font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Business Name
            </label>
            <div className={`glass-input px-4 py-3 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-gray-100'}`}>
              <p className={`text-mono ${isDark ? 'text-white' : 'text-gray-800'}`}>
                {businessInfo?.name || 'Not set'}
              </p>
            </div>
          </div>
          
          <div>
            <label className={`block text-mono text-xs font-medium mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
              Business Website
            </label>
            <div className={`glass-input px-4 py-3 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-gray-100'}`}>
              <p className={`text-mono ${isDark ? 'text-white' : 'text-gray-800'}`}>
                {businessInfo?.website || 'Not set'}
              </p>
            </div>
          </div>
        </div>
        
        <div className="mt-6 glass-alert-info rounded-lg p-4">
          <div className="flex gap-3">
            <svg className="h-5 w-5 text-blue-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <p className={`text-mono text-sm ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>
                To change your business information, please contact support.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Tracked Competitors */}
      <div className="glass-card rounded-2xl p-8 dashboard-optimized">
        <h3 className={`heading-mono text-lg text-glow mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>
          Tracked Competitors
        </h3>
        <p className={`text-mono text-sm leading-relaxed mb-6 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
          Manage the competitors you want to track across all searches. These competitors will be prioritized in your analysis results.
        </p>
        
        {/* Add Competitor Form */}
        <div className="mb-6">
          <div className="flex gap-3 mb-3">
            <input
              type="text"
              placeholder="Competitor name (required)"
              value={newCompetitor.name}
              onChange={(e) => setNewCompetitor({ ...newCompetitor, name: e.target.value })}
              className={`flex-1 glass-input px-4 py-3 rounded-lg text-mono ${
                isDark ? 'bg-gray-800/50 text-white' : 'bg-gray-100 text-gray-800'
              }`}
            />
            <input
              type="text"
              placeholder="Domain (optional)"
              value={newCompetitor.domain}
              onChange={(e) => setNewCompetitor({ ...newCompetitor, domain: e.target.value })}
              className={`flex-1 glass-input px-4 py-3 rounded-lg text-mono ${
                isDark ? 'bg-gray-800/50 text-white' : 'bg-gray-100 text-gray-800'
              }`}
            />
            <button
              onClick={handleAddCompetitor}
              disabled={addingCompetitor || !newCompetitor.name.trim()}
              className={`btn-glass-accent px-6 py-3 rounded-lg text-mono text-sm font-medium hover:scale-105 transform transition-all ${
                addingCompetitor || !newCompetitor.name.trim() ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {addingCompetitor ? 'Adding...' : 'Add Competitor'}
            </button>
          </div>
          <p className={`text-mono text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
            Make sure when you add a competitor name, that their name is written correctly
          </p>
        </div>
        
        {/* Competitors List */}
        {loadingCompetitors ? (
          <div className="text-center py-8">
            <div className="glass-spinner w-8 h-8 mx-auto mb-2"></div>
            <p className={`text-mono text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Loading competitors...
            </p>
          </div>
        ) : competitors.length > 0 ? (
          <div className="space-y-3">
            {competitors.map((competitor) => (
              <div 
                key={competitor.id} 
                className={`flex items-center justify-between p-4 rounded-lg ${
                  isDark ? 'bg-gray-800/30' : 'bg-gray-100/50'
                } hover:bg-opacity-70 transition-all`}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h4 className={`text-mono font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                      {competitor.competitor_name}
                    </h4>
                    {competitor.source === 'serpapi' && (
                      <span className="glass-badge glass-badge-info text-mono text-xs px-2 py-1">
                        AI Discovered
                      </span>
                    )}
                    {competitor.source === 'manual' && (
                      <span className="glass-badge text-mono text-xs px-2 py-1">
                        Manual
                      </span>
                    )}
                  </div>
                  {competitor.competitor_domain && (
                    <p className={`text-mono text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {competitor.competitor_domain}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => handleRemoveCompetitor(competitor.id)}
                  className={`p-2 rounded-lg ${
                    isDark ? 'hover:bg-red-500/20' : 'hover:bg-red-100'
                  } transition-all`}
                >
                  <svg className="w-5 h-5 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="glass-dark rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
              <svg className={`h-8 w-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <p className={`text-mono text-sm mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
              No competitors tracked yet
            </p>
            <p className={`text-mono text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              Add competitors above to track them across all searches
            </p>
          </div>
        )}
      </div>

      {/* Account Information */}
      <div className="glass-card rounded-2xl p-8 dashboard-optimized">
        <h3 className={`heading-mono text-lg text-glow mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>
          Account Information
        </h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center py-3 border-b border-white/10">
            <span className={`text-mono text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Email Address</span>
            <span className={`text-mono text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>{user?.email}</span>
          </div>
          <div className="flex justify-between items-center py-3 border-b border-white/10">
            <span className={`text-mono text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Full Name</span>
            <span className={`text-mono text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
              {user?.firstName} {user?.lastName}
            </span>
          </div>
          <div className="flex justify-between items-center py-3 border-b border-white/10">
            <span className={`text-mono text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Member Since</span>
            <span className={`text-mono text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
              {formatDate(user?.createdAt)}
            </span>
          </div>
          <div className="flex justify-between items-center py-3">
            <span className={`text-mono text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Account Status</span>
            <span className={`glass-badge text-mono text-sm font-medium px-3 py-1 ${
              user?.isActive ? 'glass-badge-success' : 'glass-badge-error'
            }`}>
              {user?.isActive ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
      </div>

      {/* Help */}
      <div className="glass-card rounded-2xl p-8 dashboard-optimized">
        <h3 className={`heading-mono text-lg text-glow mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>
          Help
        </h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center py-3">
            <div>
              <h4 className={`text-mono text-sm font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>
                Onboarding Status
              </h4>
              <p className={`text-mono text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Your onboarding is {user?.onboardingCompleted ? 'complete' : 'incomplete'}
              </p>
            </div>
            <span className={`glass-badge text-mono text-sm font-medium px-3 py-1 ${
              user?.onboardingCompleted ? 'glass-badge-success' : 'glass-badge-warning'
            }`}>
              {user?.onboardingCompleted ? 'Completed' : 'In Progress'}
            </span>
          </div>
        </div>
      </div>

      {/* Additional Settings Placeholder */}
      <div className="glass-card rounded-2xl p-8 dashboard-optimized">
        <h3 className={`heading-mono text-lg text-glow mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>
          Preferences
        </h3>
        <div className="text-center py-8">
          <div className="glass-dark rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
            <svg className={`h-8 w-8 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
            </svg>
          </div>
          <p className={`text-mono text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            Additional preferences coming soon
          </p>
        </div>
      </div>

      {/* Sign Out */}
      <div className="glass-card rounded-2xl p-8 dashboard-optimized">
        <h3 className={`heading-mono text-lg text-glow mb-6 ${isDark ? 'text-white' : 'text-gray-800'}`}>
          Sign Out
        </h3>
        <p className={`text-mono text-sm mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
          End your current session and return to the login page.
        </p>
        <button
          onClick={handleLogout}
          className={`
            flex items-center gap-3 px-6 py-3 rounded-lg text-mono text-sm font-medium
            ${isDark 
              ? 'bg-gray-800/50 text-gray-300 hover:bg-gray-700/50 hover:text-white border border-gray-700' 
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-900 border border-gray-300'}
            hover:scale-105 transform transition-all
          `}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  );
};

export default AccountSettings;